import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      flights: 'Flights',
      hotels: 'Hotels',
      tours: 'Tours',
      cart: 'Cart',
      login: 'Sign In',
      register: 'Sign Up',
      logout: 'Sign Out',
      admin: 'Admin',
      myBookings: 'My Bookings',
      myQuotes: 'My Quotes',
      myAgency: 'Agency Panel',
    },
    // Hero
    hero: {
      title: 'Discover the World',
      subtitle: 'Curated travel experiences for the discerning explorer',
      searchFlights: 'Search Flights',
      searchHotels: 'Search Hotels',
      exploreTours: 'Explore Tours',
    },
    // Search
    search: {
      from: 'From',
      to: 'To',
      departure: 'Departure',
      return: 'Return',
      passengers: 'Passengers',
      search: 'Search',
      destination: 'Destination',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      rooms: 'Rooms',
      guests: 'Guests',
      duration: 'Duration',
      category: 'Category',
      allCategories: 'All Categories',
      selectDate: 'Select date',
      selectDates: 'Select dates',
      checkInFirst: 'Pick check-in first',
      dateRange: 'Stay dates',
      nights: 'nights',
      apply: 'Apply',
      clear: 'Clear',
    },
    // Results
    results: {
      flights: 'Available Flights',
      hotels: 'Available Hotels',
      tours: 'Available Tours',
      noResults: 'No results found',
      from: 'from',
      perNight: 'per night',
      days: 'days',
      direct: 'Direct',
      stops: 'stops',
      addToCart: 'Add to Cart',
      viewDetails: 'View Details',
    },
    // Cart
    cart: {
      title: 'Your Selection',
      empty: 'Your cart is empty',
      emptySubtitle: 'Add flights, hotels, or tours to begin planning your journey',
      total: 'Total',
      checkout: 'Proceed to Checkout',
      remove: 'Remove',
      continueShopping: 'Continue Shopping',
    },
    // Checkout
    checkout: {
      title: 'Complete Your Booking',
      customerInfo: 'Customer Information',
      billingInfo: 'Billing Address',
      paymentInfo: 'Payment Details',
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      address: 'Street Address',
      city: 'City',
      state: 'State/Province',
      zip: 'Postal Code',
      country: 'Country',
      cardNumber: 'Card Number',
      expiry: 'Expiry',
      cvv: 'CVV',
      placeOrder: 'Place Order',
      processing: 'Processing...',
    },
    // Auth
    auth: {
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to access your bookings',
      registerTitle: 'Create Account',
      registerSubtitle: 'Join us for exclusive travel experiences',
      email: 'Email',
      password: 'Password',
      name: 'Full Name',
      login: 'Sign In',
      register: 'Create Account',
      orContinueWith: 'Or continue with',
      googleLogin: 'Continue with Google',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
    },
    // Admin
    admin: {
      dashboard: 'Dashboard',
      bookings: 'Bookings',
      stats: 'Statistics',
      totalBookings: 'Total Bookings',
      confirmedBookings: 'Confirmed',
      totalUsers: 'Total Users',
      totalRevenue: 'Total Revenue',
      recentBookings: 'Recent Bookings',
      bookingId: 'Booking ID',
      customer: 'Customer',
      status: 'Status',
      amount: 'Amount',
      date: 'Date',
      actions: 'Actions',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      back: 'Back',
      next: 'Next',
      currency: 'EUR',
    },
    // Footer
    footer: {
      tagline: 'Curated travel experiences since 2025',
      support: 'Support',
      company: 'Company',
      legal: 'Legal',
      contact: 'Contact Us',
      faq: 'FAQ',
      about: 'About Us',
      careers: 'Careers',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
  },
  es: {
    // Navigation
    nav: {
      flights: 'Vuelos',
      hotels: 'Hoteles',
      tours: 'Circuitos',
      cart: 'Carrito',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      logout: 'Cerrar Sesión',
      admin: 'Admin',
      myBookings: 'Mis Reservas',
      myQuotes: 'Mis Cotizaciones',
      myAgency: 'Panel de Agencia',
    },
    // Hero
    hero: {
      title: 'Descubre el Mundo',
      subtitle: 'Experiencias de viaje seleccionadas para el explorador exigente',
      searchFlights: 'Buscar Vuelos',
      searchHotels: 'Buscar Hoteles',
      exploreTours: 'Explorar Circuitos',
    },
    // Search
    search: {
      from: 'Origen',
      to: 'Destino',
      departure: 'Salida',
      return: 'Regreso',
      passengers: 'Pasajeros',
      search: 'Buscar',
      destination: 'Destino',
      checkIn: 'Entrada',
      checkOut: 'Salida',
      rooms: 'Habitaciones',
      guests: 'Huéspedes',
      duration: 'Duración',
      category: 'Categoría',
      allCategories: 'Todas las Categorías',
      selectDate: 'Selecciona fecha',
      selectDates: 'Selecciona fechas',
      checkInFirst: 'Selecciona entrada primero',
      dateRange: 'Fechas de estancia',
      nights: 'noches',
      apply: 'Aplicar',
      clear: 'Limpiar',
    },
    // Results
    results: {
      flights: 'Vuelos Disponibles',
      hotels: 'Hoteles Disponibles',
      tours: 'Circuitos Disponibles',
      noResults: 'No se encontraron resultados',
      from: 'desde',
      perNight: 'por noche',
      days: 'días',
      direct: 'Directo',
      stops: 'escalas',
      addToCart: 'Añadir al Carrito',
      viewDetails: 'Ver Detalles',
    },
    // Cart
    cart: {
      title: 'Tu Selección',
      empty: 'Tu carrito está vacío',
      emptySubtitle: 'Añade vuelos, hoteles o circuitos para planificar tu viaje',
      total: 'Total',
      checkout: 'Proceder al Pago',
      remove: 'Eliminar',
      continueShopping: 'Seguir Comprando',
    },
    // Checkout
    checkout: {
      title: 'Completa tu Reserva',
      customerInfo: 'Información del Cliente',
      billingInfo: 'Dirección de Facturación',
      paymentInfo: 'Datos de Pago',
      name: 'Nombre Completo',
      email: 'Correo Electrónico',
      phone: 'Teléfono',
      address: 'Dirección',
      city: 'Ciudad',
      state: 'Provincia',
      zip: 'Código Postal',
      country: 'País',
      cardNumber: 'Número de Tarjeta',
      expiry: 'Vencimiento',
      cvv: 'CVV',
      placeOrder: 'Realizar Pedido',
      processing: 'Procesando...',
    },
    // Auth
    auth: {
      loginTitle: 'Bienvenido',
      loginSubtitle: 'Inicia sesión para acceder a tus reservas',
      registerTitle: 'Crear Cuenta',
      registerSubtitle: 'Únete para experiencias de viaje exclusivas',
      email: 'Correo Electrónico',
      password: 'Contraseña',
      name: 'Nombre Completo',
      login: 'Iniciar Sesión',
      register: 'Crear Cuenta',
      orContinueWith: 'O continúa con',
      googleLogin: 'Continuar con Google',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
    },
    // Admin
    admin: {
      dashboard: 'Panel de Control',
      bookings: 'Reservas',
      stats: 'Estadísticas',
      totalBookings: 'Total Reservas',
      confirmedBookings: 'Confirmadas',
      totalUsers: 'Total Usuarios',
      totalRevenue: 'Ingresos Totales',
      recentBookings: 'Reservas Recientes',
      bookingId: 'ID Reserva',
      customer: 'Cliente',
      status: 'Estado',
      amount: 'Importe',
      date: 'Fecha',
      actions: 'Acciones',
    },
    // Common
    common: {
      loading: 'Cargando...',
      error: 'Ha ocurrido un error',
      success: 'Éxito',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      save: 'Guardar',
      back: 'Volver',
      next: 'Siguiente',
      currency: 'EUR',
    },
    // Footer
    footer: {
      tagline: 'Experiencias de viaje seleccionadas desde 2025',
      support: 'Soporte',
      company: 'Empresa',
      legal: 'Legal',
      contact: 'Contacto',
      faq: 'Preguntas Frecuentes',
      about: 'Sobre Nosotros',
      careers: 'Empleo',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
    },
  },
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  // Locked to Spanish across the whole app. The toggle is a no-op
  // until the EN copy is finalised; previous users may have a stale
  // 'en' value persisted in localStorage from older builds — wipe
  // it on mount so they don't end up half-English next visit.
  const [language, setLanguage] = useState('es');

  useEffect(() => {
    localStorage.setItem('language', 'es');
    document.documentElement.lang = 'es';
  }, []);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations.es;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    // Intentionally locked. Re-enable when EN translations are ready.
    setLanguage('es');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
